<template>
  <!-- The collection drawer's overview, still: the featured pair rising through the edge, the count, and the minis. -->
  <div class="collection-preview" aria-hidden="true">
    <div class="preview-featured">
      <span class="preview-face">
        <img :src="featured.face" alt="" />
      </span>
      <span class="preview-rank">
        <span
          v-for="(piece, index) in featuredRank"
          :key="index"
          class="preview-square"
          :class="index % 2 === 0 ? 'is-dark' : 'is-light'"
        >
          <GamChessboardPiece :piece="piece" />
        </span>
      </span>
      <p class="preview-caption">
        <span class="is-unique">#{{ featured.index }}</span> ・
        <span class="is-unique">{{ partnerName }}</span>
      </p>
    </div>

    <p class="preview-count">
      <span class="preview-count-value">{{ pairs.length }}</span>
      <span class="preview-count-rest">{{ localize('handshake.collection.progress') }}</span>
    </p>

    <div class="preview-minis">
      <span
        v-for="(slot, index) in minis"
        :key="index"
        class="preview-bond"
        :class="{ 'is-empty': !slot, 'is-chosen': slot === featured }"
      >
        <span class="preview-bond-square">{{ slot ? `#${slot.index}` : '#' }}</span>
        <span class="preview-bond-face">
          <img v-if="slot" :src="slot.face" alt="" />
          <GamIcon v-else :name="GamIconName.PAWN_EMPTY" size="11px" is-current />
        </span>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { localize } from '@/core/gambits';
import GamChessboardPiece from '@/views/components/gam-chessboard/GamChessboardPiece.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { backRankFromNumber } from '@/views/main/handshakes/utils/handshake-game';
import { computed } from 'vue';

export interface MasterCollectionPair {
  /** The Chess960 number played. */
  index: number;
  /** The person it was played with. */
  face: string;
}

const props = defineProps<{
  /** The pairs collected so far; the first is the featured one. */
  pairs: MasterCollectionPair[];
  /** The featured pair's player, as their handle. */
  partnerName: string;
}>();

/** Two rows of the drawer's four, so the empty seats show how much is left. */
const MINI_SLOTS = 8;

const featured = computed(() => props.pairs[0]);
const featuredRank = computed(() => backRankFromNumber(featured.value.index));
const minis = computed(() =>
  Array.from({ length: MINI_SLOTS }, (_, index): MasterCollectionPair | null => props.pairs[index] ?? null),
);
</script>

<style lang="scss" scoped>
/* The drawer's card: its dark and rounding, with room on top for the face to rise through the edge. */
.collection-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xl);
  box-sizing: border-box;
  width: min(100%, 420px);
  margin-top: 38px;
  padding: 0 var(--spacing-xl) var(--spacing-xl);
  border: 1px solid var(--color-white-10);
  border-radius: 1.5rem;
  background: var(--color-dark-600);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.55);
}

.preview-featured {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  width: min(100%, 300px);
  margin-top: -38px;
}

.preview-face {
  z-index: 0;
  display: flex;
  width: 76px;
  height: 76px;
  margin-bottom: -10px;
  overflow: hidden;
  border-radius: 50%;
  background: var(--color-dark-500);
  box-shadow:
    0 0 0 3px var(--color-dark-600),
    0 8px 20px rgba(0, 0, 0, 0.35);

  img {
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    padding: 14%;
    object-fit: contain;
  }
}

/* The recommendation's rank: white's back rank on the board's two browns. */
.preview-rank {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  width: 100%;
  aspect-ratio: 8 / 1;
  overflow: hidden;
  border-radius: var(--radius-x-small);
}

.preview-square {
  display: flex;
  align-items: center;
  justify-content: center;

  &.is-light {
    background: #f0d9b5;
  }

  &.is-dark {
    background: #b58863;
  }
}

.preview-caption {
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.05em;
  color: var(--color-white-50);

  .is-unique {
    color: var(--color-pink);
  }
}

.preview-count {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
  color: var(--color-white-40);
}

.preview-count-value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
  color: var(--color-pink);
}

.preview-count-rest {
  margin-left: 0.35em;
}

.preview-minis {
  display: grid;
  grid-template-columns: repeat(4, max-content);
  justify-content: center;
  gap: 0.85rem 1.1rem;
}

/* A pair: the position's square tucked over the face it was played with. */
.preview-bond {
  --pair-face: 36px;
  display: flex;
  align-items: center;
}

.preview-bond-square,
.preview-bond-face {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: var(--pair-face);
  height: var(--pair-face);
  overflow: hidden;
}

.preview-bond-square {
  z-index: 1;
  margin-right: calc(var(--pair-face) * -0.28);
  padding: 0 0.12em;
  box-sizing: border-box;
  border-radius: calc(var(--pair-face) * 0.12);
  background: var(--color-pink);
  box-shadow: 0 0 0 2px var(--color-dark-600);
  color: var(--color-white);
  font-size: 9px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.preview-bond-face {
  border-radius: 50%;
  background: var(--color-dark-500);
  color: var(--color-white);

  img {
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    padding: 14%;
    object-fit: contain;
  }
}

.preview-bond.is-empty {
  .preview-bond-square,
  .preview-bond-face {
    background: var(--color-not-played);
    color: var(--color-white-30);
  }
}

.preview-bond.is-chosen .preview-bond-square {
  box-shadow: 0 0 0 2px var(--color-white);
}
</style>
